import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  plus,
  location,
  participant,
} from "../../assets/Exportimageicon";

import {
  apiGet,
  apiDelete,
} from "../../Services/Services.js";

import Pagination from "../../Components/pagination/Pagination";

import "./Mission.css";

const StatusBadge = ({ status }) => {
  const safeStatus = status || "--";

  return (
    <span
      className={`badge badge--${safeStatus
        .toLowerCase()
        .replace(/\s+/g, "-")}`}
    >
      {safeStatus}
    </span>
  );
};

function Mission() {
  const navigate = useNavigate();

  const [challenges, setChallenges] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [deleteModalOpen, setDeleteModalOpen] =
    useState(false);

  // Store selected mission ID here
  const [selectedMissionId, setSelectedMissionId] =
    useState(null);

  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [currentPage, perPage]);

  const fetchData = async () => {
    try {
      const res = await apiGet(
        `/admin/mission-management-challenges?page=${currentPage}&limit=${perPage}`
      );

      const responseData = res?.data || {};

      setChallenges(responseData?.data || []);
      setCurrentPage(responseData?.currentPage || 1);
      setTotalItems(responseData?.totalItems || 0);
      setTotalPages(responseData?.totalPages || 1);
    } catch (error) {
      console.error("Mission list error:", error);
    }
  };

  const handleView = (id) => {
    if (!id) return;

    navigate(`/mission-view/${id}`);
  };

  // Open modal and save mission ID
  const openDeleteModal = (id) => {
    if (!id) return;

    setSelectedMissionId(id);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;

    setDeleteModalOpen(false);
    setSelectedMissionId(null);
  };

  const handleDeleteMission = async () => {
    if (!selectedMissionId || isDeleting) return;

    try {
      setIsDeleting(true);

      await apiDelete(
        `/admin/delete-challenge/${selectedMissionId}`,
        {},
        {},
        {
          showSuccessToast: true,
          useApiSuccessMessage: true,
          successMessage: "Mission deleted successfully",
        }
      );

      setDeleteModalOpen(false);
      setSelectedMissionId(null);

      /*
       * If the last mission on the current page was deleted,
       * move to the previous page when possible.
       */
      if (challenges.length === 1 && currentPage > 1) {
        setCurrentPage((previousPage) => previousPage - 1);
      } else {
        await fetchData();
      }
    } catch (error) {
      console.error("Delete mission error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="marketing__card">
        {/* Card Header */}
        <div className="marketing__card-header">
          <div>
            <h1 className="settings__title">
              Mission Management
            </h1>

            <p className="settings__subtitle">
              Create and manage location-based missions
            </p>
          </div>

          <button
            type="button"
            className="marketing__create-btn"
            onClick={() => navigate("/add-mission")}
          >
            <img src={plus} alt="" />
            Create New Mission
          </button>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="marketing-table">
            <thead>
              <tr>
                <th>Mission Name</th>
                <th>Location</th>
                <th>Distance (km)</th>
                <th>Duration</th>
                <th>Participants</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {challenges.length > 0 ? (
                challenges.map((row) => (
                  <tr
                    key={row.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleView(row.id)}
                  >
                    <td className="td--bold">
                      {row.missionName || "--"}
                    </td>

                    <td>
                      <img
                        src={location}
                        alt=""
                        style={{ color: "#666" }}
                      />

                      <span className="ms-2">
                        {row.location || "--"}
                      </span>
                    </td>

                    <td className="td--muted">
                      {row.distanceKm ?? "--"}
                    </td>

                    <td>{row.duration || "--"}</td>

                    <td>
                      <img src={participant} alt="" />

                      <span className="ms-2">
                        {row.participants ?? 0}
                      </span>
                    </td>

                    <td>
                      <StatusBadge
                        status={row.status?.toUpperCase()}
                      />
                    </td>

                    <td>
                      <button
                        type="button"
                        className="delete-btn"
                        onClick={(event) => {
                          // Prevent opening mission details
                          event.stopPropagation();

                          openDeleteModal(row.id);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: "center",
                      padding: "30px",
                    }}
                  >
                    No missions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <Pagination
            variant="full"
            summary={`Showing ${
              totalItems === 0
                ? 0
                : (currentPage - 1) * perPage + 1
            }-${Math.min(
              currentPage * perPage,
              totalItems
            )} of ${totalItems}`}
            page={currentPage}
            totalPages={totalPages}
            totalCount={totalItems}
            perPage={perPage}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
            onPerPageChange={(value) => {
              setPerPage(value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="custom-modal-overlay">
          <div className="custom-modal-content">
            <div className="custom-modal-header">
              <h2>Delete Mission</h2>

              <button
                type="button"
                className="close-modal-btn"
                onClick={closeDeleteModal}
                disabled={isDeleting}
              >
                ×
              </button>
            </div>

            <form
              className="custom-modal-form"
              onSubmit={(event) => {
                event.preventDefault();
                handleDeleteMission();
              }}
            >
              <p className="fs-5">
                Are you sure you want to delete this mission?
              </p>

              <div className="custom-modal-footer">
                <button
                  type="button"
                  className="btn-modal-draft"
                  onClick={closeDeleteModal}
                  disabled={isDeleting}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn-modal-send"
                  disabled={isDeleting}
                >
                  {isDeleting
                    ? "Deleting..."
                    : "Delete"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Mission;